import { IconButton } from '@design-system/components/button/iconButton';
import {
  faCaretLeft,
  faCaretRight,
  faCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode, useState } from 'react';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import { Typography } from '@design-system/components/typography/typography';
import { Modal } from '@design-system/components/modal/modal';
import { Tag } from '@design-system/components/tag/tag';
import guideStepLearnImage from '/static/images/guide-step-learn.png';
import guideStepThreeImage from '/static/images/pricing-guide-step-3.png';

type StepValues = 0 | 1 | 2;

export const PricingGuide = ({ disabled }: { disabled?: boolean }) => {
  const [step, setStep] = useState<StepValues>(0);
  const [isOpen, setOpen] = useState(false);

  return (
    <>
      <Typography
        className={clsx(
          disabled ? 'cursor-default' : 'cursor-pointer',
          'px-4 py-2',
          sprinkles({ color: disabled ? 'N-60' : 'P-80' }),
        )}
        onClick={disabled ? undefined : () => setOpen(true)}
      >
        Pricing guide
      </Typography>
      <Modal
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        title={getTitleByStep(step)}
        modalClassName="w-[900px]"
        contentClassName="gap-[39px]"
      >
        <div>
          <div className="flex gap-2 h-[422px]">
            <IconButton
              icon={faCaretLeft}
              variant="text"
              className="h-full"
              onClick={() =>
                step > 0
                  ? setStep((prev) => (prev - 1) as StepValues)
                  : setStep(2)
              }
            />
            <div className="w-full h-full">
              <Steps step={step} />
            </div>
            <IconButton
              icon={faCaretRight}
              variant="text"
              className="h-full"
              onClick={() =>
                step < 2
                  ? setStep((prev) => (prev + 1) as StepValues)
                  : setStep(0)
              }
            />
          </div>
          <StepIndicator active={step} change={(value) => setStep(value)} />
        </div>
      </Modal>
    </>
  );
};

const StepIndicator = ({
  active,
  change,
}: {
  active: StepValues;
  change: (value: StepValues) => void;
}) => (
  <div className="flex gap-2.5 items-center justify-center">
    {Array.from({ length: 3 }, (_, i) => (
      <FontAwesomeIcon
        icon={faCircle}
        key={i}
        className={clsx(
          'cursor-pointer',
          active === i && sprinkles({ color: 'P-80', fontSize: 'm' }),
          active !== i && sprinkles({ color: 'N-60', fontSize: 'xs' }),
        )}
        onClick={() => change(i as StepValues)}
      />
    ))}
  </div>
);

const getTitleByStep = (step: 0 | 1 | 2): string => {
  switch (step) {
    case 0:
      return 'Variations & pricing';
    case 1:
      return 'Learn from others';
    case 2:
      return 'Value stacking to increase price perception';
  }
};

const Steps = ({ step }: { step: 0 | 1 | 2 | 3 }): ReactNode => {
  switch (step) {
    case 0:
      return (
        <div className="flex flex-col gap-4 overflow-y-scroll h-[345px]">
          {Step3Variations.map((v) => (
            <div
              key={v.title}
              className={clsx(
                'flex flex-col p-3 gap-4 rounded-[10px]',
                sprinkles({ bg: 'N-10' }),
              )}
            >
              <div className="flex justify-between items-center">
                <Typography variant="headline" size="small">
                  {v.title}
                </Typography>
                <div className="flex gap-2">
                  {v.recommendation ? (
                    <Tag
                      label="BEST TO START"
                      className={sprinkles({
                        bg: 'SUCCESS-100',
                        color: 'W-100',
                      })}
                      readonly={true}
                    />
                  ) : null}
                  {v.tags.map((t) => (
                    <Tag
                      key={t}
                      label={t}
                      className={sprinkles({ bg: 'P-20' })}
                      readonly={true}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-4 justify-start items-center">
                <div className="max-w-[50%]">
                  <Typography variant="body" size="large">
                    {v.details}
                  </Typography>
                </div>
                <div className="flex flex-col max-w-[50%]">
                  <Typography color="P-80">BUNDLE WITH:</Typography>
                  <Typography>{v.bundle}</Typography>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    case 1:
      return (
        <div>
          <img src={guideStepLearnImage}></img>
        </div>
      );
    case 2:
      return (
        <div className="flex flex-col h-full gap-4 relative">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Typography variant="body" size="large">
                Value is the perception of getting a lot for little:
              </Typography>
              <Typography
                variant="headline"
                size="medium"
                className={clsx(
                  sprinkles({ color: 'P-80', fontFamily: 'caveat' }),
                )}
              >
                “My offering would cost $1,500, but you get it today for $200!”
              </Typography>
            </div>
            <Typography variant="body" size="large">
              How can you make product appear as valuable as possible? 🤔
              Consider below tips:
            </Typography>
          </div>
          <div
            className={clsx('p-6 rounded-[10px]', sprinkles({ bg: 'P-20' }))}
          >
            <ul className={clsx('list-["👉"] ml-6', sprinkles({ bg: 'P-20' }))}>
              {Step2.map((i) => (
                <li key={i}>
                  <Typography className="pl-2 mb-4" variant="body" size="large">
                    {i}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
          <img
            src={guideStepThreeImage}
            className="h-[185px] absolute right-0 bottom-0"
          ></img>
        </div>
      );
  }
};

const Step2 = [
  'Add ons of grocery lists, meal plan, supplement guide, habit tracker, etc.',
  'Turn the program into a challenge with prizes for winners at the end',
  'Give access to a weekly zoom call you will host',
];

const Step3Variations = [
  {
    title: 'Lead Gen',
    details:
      'Good for testing your audience to see how interested they are in purchasing your programs.',
    bundle: 'Recipes sold individually or not included in the program.',
    recommendation: true,
    tags: ['1-2 weeks', '$0-5'],
  },
  {
    title: 'Low-Ticket',
    details: 'This is a quality program that your consumers are eager for.',
    bundle: "Doesn't need nutrition if you don't want to offer.",
    tags: ['2-4 weeks', '$5-50'],
  },
  {
    title: 'High-Ticket',
    details:
      'In-depth programs targeting a specific audience who knows exactly what they are purchasing and understand the value.',
    bundle: 'Full meal plan with recipes, grocery list, and daily schedule.',
    tags: ['2-4 weeks', '$5-50'],
  },
  {
    title: 'Challenge',
    details:
      'Sell your program as normal and have this purchase be considered a "challenge entry fee" making a user eligible to win prizes.',
    bundle: 'Nutrition coaching videos can be included in the program.',
    tags: ['2-4 weeks', '$5-50'],
  },
];
